import { useEffect, useRef, useState } from 'react';
import { useWebSocket } from '../../../contexts/WebSocketContext';

/** At most one refetch per this many ms while a dig is landing findings. */
export const REFRESH_EVERY_MS = 15_000;

/** The events that can change what a table or report draws. */
const MOVES_THE_BOARD = new Set(['item', 'tag', 'run']);

/**
 * A number that goes up when this room's board has changed, at most once every
 * 15 seconds. Put it in a fetch effect's dependencies to refetch on it.
 *
 * Cards that fetch for themselves (list cards, reports) used to stay frozen
 * while a dig ran: only a chat turn reloaded them. A dig can land hundreds of
 * items, so this waits rather than refetching on every one.
 */
export function useBoardRefresh(boardroomId: number, enabled = true): number {
  const { subscribe } = useWebSocket();
  const [tick, setTick] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Off when the page already drives refreshes (the Reports tab does).
    if (!enabled) return;
    const off = subscribe((event: any) => {
      if (event?.event_type !== 'boardroom' || event.boardroom_id !== boardroomId) return;
      if (!MOVES_THE_BOARD.has(event.status)) return;
      if (timer.current) return; // one already waiting
      timer.current = setTimeout(() => {
        timer.current = null;
        setTick(t => t + 1);
      }, REFRESH_EVERY_MS);
    });
    return () => {
      off?.();
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [subscribe, boardroomId, enabled]);

  return tick;
}

export default useBoardRefresh;
